/** Zugriff auf die Worker-API. */

import type { Entry, Settings, TripResult, Person } from '@baba/engine/types';
import { verkleinereBild } from './bild.ts';

/**
 * Adresse der API.
 *
 * Leer bedeutet "gleiche Herkunft" - so läuft es beim Entwickeln, wo Vite
 * /api an den lokalen Worker weiterleitet. Für die Auslieferung liegen App
 * (Cloudflare Pages) und API (Worker) auf verschiedenen Adressen; dann wird
 * beim Bauen VITE_API_BASIS gesetzt, etwa
 * https://baba-big.dein-name.workers.dev
 */
export const API_BASIS: string = (
  // Der optionale Zugriff ist nötig, damit die Datei auch ausserhalb von Vite
  // lädt - die Tests laufen unter Node, wo import.meta.env nicht existiert.
  (import.meta as { env?: Record<string, string | undefined> }).env?.[
    'VITE_API_BASIS'
  ] ?? ''
).replace(/\/$/, '');

/** Baut eine vollständige API-Adresse. */
export function apiUrl(pfad: string): string {
  return `${API_BASIS}${pfad}`;
}

const WORT_SCHLUESSEL = 'baba.wort';
const ICH_SCHLUESSEL = 'baba.ich';

export function holeWort(): string | null {
  return localStorage.getItem(WORT_SCHLUESSEL);
}

export function setzeWort(wort: string | null): void {
  if (wort) localStorage.setItem(WORT_SCHLUESSEL, wort);
  else localStorage.removeItem(WORT_SCHLUESSEL);
}

export function holeIch(): Person {
  const v = localStorage.getItem(ICH_SCHLUESSEL);
  return v === 'otto' ? 'otto' : 'michi';
}

export function setzeIch(wer: Person): void {
  localStorage.setItem(ICH_SCHLUESSEL, wer);
}

export interface FotoInfo {
  id: string;
  entryId: string;
  feld: string | null;
  contentType: string | null;
  createdAt: string;
}

export interface Zustand {
  settings: Settings;
  entries: (Entry & { meta: { updatedAt: string; updatedBy: string | null } })[];
  fotos: FotoInfo[];
}

export class ApiFehler extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

/**
 * Kopfzeilen für jeden Aufruf: das Zugangswort und wer gerade am Gerät sitzt.
 *
 * Genau diese Kopfzeilen sind der Grund, warum weder der CSV-Export noch ein
 * Foto über einen blossen Link gehen kann - der Browser schickt bei <a href>
 * und <img src> keine eigenen Kopfzeilen mit, und der Worker antwortet mit 401.
 */
function authKopf(weitere?: HeadersInit): Headers {
  const kopf = new Headers(weitere);
  kopf.set('X-Baba-Wort', holeWort() ?? '');
  kopf.set('X-Baba-Wer', holeIch());
  return kopf;
}

async function ruf<T>(pfad: string, init: RequestInit = {}): Promise<T> {
  const kopf = authKopf(init.headers);
  if (init.body && !(init.body instanceof FormData)) {
    kopf.set('Content-Type', 'application/json');
  }

  const antwort = await fetch(apiUrl(pfad), { ...init, headers: kopf });
  if (!antwort.ok) {
    const daten = (await antwort.json().catch(() => ({}))) as {
      fehler?: string;
    };
    throw new ApiFehler(
      daten.fehler ?? 'Die Verbindung zum Server hat nicht geklappt.',
      antwort.status,
    );
  }
  return (await antwort.json()) as T;
}

export const api = {
  async anmelden(wort: string): Promise<void> {
    const antwort = await fetch(apiUrl('/api/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wort }),
    });
    if (!antwort.ok) {
      throw new ApiFehler('Zugangswort stimmt nicht.', antwort.status);
    }
    setzeWort(wort);
  },

  ladeZustand: () => ruf<Zustand>('/api/state'),

  speichereSettings: (s: Settings) =>
    ruf<{ settings: Settings }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(s),
    }),

  legeAn: (e: Partial<Entry>) =>
    ruf<{ entry: Entry }>('/api/entries', {
      method: 'POST',
      body: JSON.stringify(e),
    }),

  aendere: (id: string, e: Partial<Entry> & { updatedAt?: string }) =>
    ruf<{ konflikt: boolean; zuletztGeaendertVon: string | null }>(
      `/api/entries/${id}`,
      { method: 'PUT', body: JSON.stringify(e) },
    ),

  loesche: (id: string) =>
    ruf<{ ok: true }>(`/api/entries/${id}`, { method: 'DELETE' }),

  stelleWiederHer: (id: string) =>
    ruf<{ ok: true }>(`/api/entries/${id}/restore`, { method: 'POST' }),

  /** Unwiderruflich. Der Worker lässt das nur aus dem Papierkorb heraus zu. */
  loescheEndgueltig: (id: string) =>
    ruf<{ ok: true }>(`/api/entries/${id}/endgueltig`, { method: 'DELETE' }),

  schliesseAb: (id: string) =>
    ruf<{ snapshot: TripResult }>(`/api/entries/${id}/finalisieren`, {
      method: 'POST',
    }),

  oeffneWieder: (id: string) =>
    ruf<{ ok: true }>(`/api/entries/${id}/finalisieren`, { method: 'DELETE' }),

  /**
   * Zählerstand aus einem Foto lesen lassen (R-27).
   *
   * Das Bild wird nur gelesen, nicht gespeichert - der Vorgang existiert zu
   * diesem Zeitpunkt noch gar nicht. Verkleinert wird es trotzdem: es geht
   * über dieselbe Mobilfunkverbindung wie alles andere.
   */
  async liesZaehlerstand(
    datei: File,
  ): Promise<{ kmStand: number | null; gelesen: string }> {
    const form = new FormData();
    form.set('datei', await verkleinereBild(datei));
    return ruf<{ kmStand: number | null; gelesen: string }>('/api/ocr', {
      method: 'POST',
      body: form,
    });
  },

  async ladeFotoHoch(entryId: string, feld: string, datei: File): Promise<void> {
    const form = new FormData();
    form.set('entryId', entryId);
    form.set('feld', feld);
    form.set('datei', await verkleinereBild(datei));
    await ruf<{ id: string }>('/api/photos', { method: 'POST', body: form });
  },

  /**
   * Ein Foto als Blob holen.
   *
   * Notwendig aus demselben Grund wie beim CSV-Export: <img src> geht ohne
   * X-Baba-Wort hinaus und liefe in ein 401. Die Ansicht macht daraus ein
   * kurzlebiges Objekt-URL.
   */
  async holeFoto(id: string): Promise<Blob> {
    const antwort = await fetch(apiUrl(`/api/photos/${id}`), {
      headers: authKopf(),
    });
    if (!antwort.ok) {
      throw new ApiFehler('Das Foto liess sich nicht laden.', antwort.status);
    }
    return antwort.blob();
  },

  loescheFoto: (id: string) =>
    ruf<{ ok: true }>(`/api/photos/${id}`, { method: 'DELETE' }),

  /**
   * CSV holen und als Datei anbieten.
   *
   * Ein blosser Link funktioniert hier nicht: Der Browser folgt ihm ohne die
   * Kopfzeile X-Baba-Wort, und der Worker antwortet mit 401. Deshalb wird die
   * Datei mit gesetztem Zugangswort geholt und über ein kurzlebiges
   * Objekt-URL zum Speichern gereicht.
   */
  ladeCsvHerunter: async (): Promise<void> => {
    const antwort = await fetch(apiUrl('/api/export.csv'), {
      headers: authKopf(),
    });
    if (!antwort.ok) {
      throw new ApiFehler('Der Export hat nicht geklappt.', antwort.status);
    }

    const blob = await antwort.blob();
    const url = URL.createObjectURL(blob);
    const heute = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `baba-big-${heute}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Erst nach dem Klick freigeben, sonst bricht Safari den Download ab.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
  fotoUrl: (id: string) => apiUrl(`/api/photos/${id}`),
};
